import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Box,
  Grid,
  Typography,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
} from "@mui/material";
import RoomStatusDialog from "./roomStatus";
import PrecautionsDialog from "./PrecautionsDialog";
import { updatePatientRecord } from "../../../service/endpoint-manager";
import DialogConfirm from "./DialogConfirm";
import styles from "../../../styles.module.css";

import { getReadableTextColor } from "@thequantumhub/iam-package";

import clsx from "clsx";
import PatientTablePDF from "./patientTablePDF";
import { AuthContext } from "../../../context/AuthContext";
import { MAX_DROPDOWN_HEIGHT } from "../../../types/micellinous-constants";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSnackbar } from "notistack";
import { viewportContext } from "../../../context/ViewportContext";

const precautionsOptions = ["Contact", "Droplet"];

const PatientTableRow = ({
  row,
  index,
  onUpdate,
  tableData,
  newPatientName,
  setNewPatientName,
  prevPatientName,
  setPrevPatientName,
  setCurrentPatientId,
  setIsRoomOccupiedWarningOpen,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { screenType } = useContext(viewportContext);
  const { userGroup } = useContext(AuthContext);
  const [selectedPrecautions, setSelectedPrecautions] = useState(
    row.Precautions || []
  );
  const [editableRow, setEditableRow] = useState({ ...row });
  const [isRoomStatusDialogOpen, setIsRoomStatusDialogOpen] = useState(false);
  const [isPrecautionsDialogOpen, setIsPrecautionsDialogOpen] = useState(false);
  const [roomStatus, setRoomStatus] = useState(row["RoomStatus"]);
  const [selectedColor, setSelectedColor] = useState(
    row?.Color ? row?.Color : "white"
  );

  const [localRoom, setLocalRoom] = useState(row.Room);
  const [aide, setAide] = useState(row.Aide);

  const [localPatient, setLocalPatient] = useState(row.Patient);
  const [isNameConflict, setIsNameConflict] = useState(false);
  const [isDeleteClicked, setIsDeleteClicked] = useState({
    status: false,
    id: null,
    name: null,
  });

  const [localAlerts, setLocalAlerts] = useState(row.Alerts);
  const [isolationStartDate, setIsolationStartDate] = useState(
    row.IsolationStartDate
  );

  const mapFieldName = (fieldName, direction) => {
    const mapping = {
      Room: "room",
      NextRound: "nextRound",
      Patient: "patient",
      Provider: "provider",
      Nurse: "nurse",
      Aide: "aide",
      Precautions: "infotiles",
      Alerts: "alerts",
      RoomStatus: "roomStatus",
      Education: "educations",
      Color: "color",
      IsolationStartDate: "isolationStartDate",
    };

    if (direction === "toApi") {
      return mapping[fieldName] || fieldName;
    } else {
      return (
        Object.keys(mapping).find((key) => mapping[key] === fieldName) ||
        fieldName
      );
    }
  };

  const getPrecautionBackgroundColor = (precaution) => {
    const colorMapping = {
      Contact: "linear-gradient(rgb(193, 178, 230), rgb(115, 82, 199))",
      Droplet: "linear-gradient(rgb(163, 234, 211), rgb(59, 210, 162))",
      "Contact Droplet Plus":
        "linear-gradient(rgb(249, 204, 138), rgb(243, 151, 17))",
    };

    return colorMapping[precaution] || "inherit";
  };

  const handleOpenRoomOccupiedWarning = () => {
    setIsRoomOccupiedWarningOpen(true);
  };

  const handleCloseRoomOccupiedWarning = () => {
    setIsRoomOccupiedWarningOpen(false);
  };
  const handleEditChange = async (field, value) => {
    let payloadValue;

    switch (field) {
      case "Alerts":
        payloadValue = value;
        break;
      case "Education":
        payloadValue = parseInt(value, 10);
        break;
      case "Precautions":
        payloadValue = value;
        break;
      case "NextRound":
        payloadValue = value;
        break;
      default:
        payloadValue = value;
    }

    const apiField = mapFieldName(field, "toApi");
    const updatedRow = { ...editableRow, [apiField]: payloadValue };
    setEditableRow(updatedRow);

    if (field === "Room") {
      const roomExists = tableData.some(
        (otherRow) => otherRow.Room === payloadValue && otherRow.id !== row.id
      );

      if (roomExists) {
        setIsRoomOccupiedWarningOpen(true);
        try {
          await updatePatientRecord(row.id, { [apiField]: payloadValue });
          // !! DOES NOT UPDATE PROPERLY - TABLE NOT UPDATED
          onUpdate(row.id, field, payloadValue);
        } catch (error) {
          console.error("Error updating patient record:", error);
        }
        return;
      }
    }
    if (field === "Patient") {
      const nameExists = tableData.some(
        (otherRow) => otherRow.Patient === value && otherRow.id !== row.id
      );

      if (nameExists) {
        setIsNameConflict(true);
        setNewPatientName(value);
        setPrevPatientName(tableData[index].Patient);
        setCurrentPatientId(row.id);
        return;
      }
    }
    const payload = { [apiField]: payloadValue };

    try {
      await updatePatientRecord(row.id, payload);
      onUpdate(row.id, field, payloadValue);
    } catch (error) {
      console.error("Error updating patient record:", error);
    }
  };

  const handleNameConflictConfirmation = async (confirm) => {
    setIsNameConflict(false);

    if (confirm) {
      try {
        await updatePatientRecord(row.id, {
          patient: newPatientName,
        });
        onUpdate(row.id, "Patient", newPatientName);
      } catch (error) {
        console.error("Error updating patient record:", error);
      }
    } else {
      setLocalPatient(prevPatientName);
      onUpdate(row.id, "Patient", prevPatientName);
    }

    setCurrentPatientId(null);
  };

  const handleRoomStatusClick = () => {
    setIsRoomStatusDialogOpen(true);
  };

  const handleRoomStatusSelect = (newStatus) => {
    setRoomStatus(newStatus);
    handleEditChange("RoomStatus", newStatus);
    setIsRoomStatusDialogOpen(false);
  };

  const handlePrecautionsSelect = (newPrecautions) => {
    setSelectedPrecautions(newPrecautions);
    handleEditChange("Precautions", newPrecautions);
    setIsPrecautionsDialogOpen(false);
  };

  const handlePrecautionsClick = () => {
    setIsPrecautionsDialogOpen(true);
  };

  const handleColorChange = (event) => {
    const color = event.target.value;
    setSelectedColor(color);
    handleEditChange("color", color);
  };

  const handleDelete = async (id, confirm) => {
    if (!confirm) {
      setIsDeleteClicked({ status: false, id: null, name: null });
    } else {
      try {
        const response = await updatePatientRecord(id, { archived: true });
        onUpdate(row.id, "archived", true);
        enqueueSnackbar(`${response?.data?.message}`, {
          variant: "success",
        });

        setIsDeleteClicked({ status: false, id: null, name: null });
      } catch (error) {
        console.error("Error updating patient record:", error);
      }
    }
  };
  return (
    <>
      {!tableData[index].archived && (
        <TableRow
          key={row.id}
          style={{
            backgroundColor: selectedColor,
            borderBottom: "1px solid rgba(224, 224, 224, 1)",
          }}
          className={"patientTable"}
        >
          <TableCell
            style={{ padding: "0px !important" }}
            className={styles.patientTableCellRoom}
          >
            <TextField
              value={localRoom}
              size="small"
              fullWidth
              onChange={(e) => setLocalRoom(e.target.value)}
              onBlur={() => handleEditChange("Room", localRoom)}
              variant="standard"
              className={styles.patientInputField}
              InputProps={{
                disableUnderline: true,
                inputProps: {
                  style: {
                    textAlign: "center",
                    fontSize: screenType.isTv ? ".875rem" : ".875rem",
                    fontWeight: 500,
                  },
                },
                readOnly: !userGroup?.isAdmin,
              }}
            />
          </TableCell>

          <TableCell
            style={{ padding: "0px !important" }}
            className={styles.patientTableCellName}
          >
            <TextField
              value={localPatient}
              onChange={(e) => setLocalPatient(e.target.value)}
              onBlur={() => handleEditChange("Patient", localPatient)}
              size="small"
              fullWidth
              variant="standard"
              style={{ padding: "0px !important" }}
              className={styles.patientInputField}
              InputProps={{
                disableUnderline: true,
                inputProps: {
                  style: {
                    textAlign: "center",
                    fontSize: screenType.isTv ? ".875rem" : ".875rem",
                    fontWeight: 500,
                  },
                },
                readOnly: !userGroup?.isAdmin,
              }}
            />
          </TableCell>
          <TableCell
            style={{ padding: "0px" }}
            className={styles.patientTableCellRoom}
          >
            <TextField
              value={aide}
              size="small"
              fullWidth
              onChange={(e) => setAide(e.target.value)}
              onBlur={() => handleEditChange("Aide", aide)}
              variant="standard"
              className={styles.patientInputField}
              InputProps={{
                disableUnderline: true,
                inputProps: {
                  style: {
                    textAlign: "center",
                    fontSize: screenType.isTv ? ".875rem" : ".875rem",
                    fontWeight: 500,
                  },
                },
                readOnly: !userGroup?.isAdmin,
              }}
            />
          </TableCell>
          <TableCell
            onClick={userGroup?.isAdmin && handlePrecautionsClick}
            style={{
              cursor: userGroup?.isAdmin && "pointer",
              padding: "0px !important",
            }}
            className={styles.patientTableCell}
          >
            {selectedPrecautions.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                {selectedPrecautions.map((precaution) => {
                  var backgroundColor =
                    getPrecautionBackgroundColor(precaution);
                  return (
                    <span
                      key={precaution}
                      style={{
                        background: backgroundColor,
                        color: getReadableTextColor(backgroundColor),
                      }}
                      className={styles.precautionTileStyling}
                    >
                      {precaution}
                    </span>
                  );
                })}
              </div>
            ) : (
              <div>
                {userGroup?.isAdmin && (
                  <div className={styles.patientAddPrecautionContainer}>
                    Click to Add
                  </div>
                )}
              </div>
            )}
          </TableCell>
          <TableCell
            style={{ padding: "0px !important" }}
            className={styles.patientTableDateCell}
          >
            <TextField
              value={isolationStartDate}
              onChange={(e) => setIsolationStartDate(e.target.value)}
              onBlur={() =>
                handleEditChange("IsolationStartDate", isolationStartDate)
              }
              size="small"
              type="date"
              fullWidth
              variant="standard"
              className={styles.patientDateField}
              style={{ padding: "0px !important" }}
              InputProps={{
                // disableUnderline: true,
                inputProps: {
                  style: {
                    textAlign: "center",
                    fontSize: screenType.isTv ? ".875rem" : ".875rem",
                    fontWeight: 500,
                  },
                },
                readOnly: !userGroup?.isAdmin,
              }}
            />
          </TableCell>

          <TableCell
            style={{
              cursor: "pointer",
              backgroundColor: selectedColor,
              color: "black",
              padding: "0px !important",
            }}
            className={styles.patientTableCellAlerts}
          >
            <TextField
              value={localAlerts}
              onChange={(e) => setLocalAlerts(e.target.value)}
              onBlur={() => handleEditChange("Alerts", localAlerts)}
              size="small"
              fullWidth
              variant="standard"
              className={styles.patientInputField}
              style={{ padding: "0px !important" }}
              InputProps={{
                disableUnderline: true,
                inputProps: {
                  style: {
                    textAlign: "center",
                    fontSize: screenType.isTv ? ".875rem" : ".875rem",
                    fontWeight: 500,
                  },
                },
                readOnly: !userGroup?.isAdmin,
              }}
            />
          </TableCell>

          {userGroup?.isAdmin && (
            <TableCell
              className={styles.patientTableCellColor}
              style={{ padding: "0px !important" }}
            >
              <FormControl>
                <Select
                  value={selectedColor}
                  onChange={handleColorChange}
                  displayEmpty
                  inputProps={{ "aria-label": "Select Color" }}
                  readOnly={!userGroup?.isAdmin}
                  className={"colorPicker"}
                >
                  <MenuItem value="white">
                    <div className={styles.colorPickerWhite}></div>
                  </MenuItem>
                  <MenuItem value="red">
                    <div className={styles.colorPickerRed}></div>
                  </MenuItem>
                  <MenuItem value="yellow">
                    <div className={styles.colorPickerYellow}></div>
                  </MenuItem>
                  <MenuItem value="green">
                    <div className={styles.colorPickerGreen}></div>
                  </MenuItem>
                </Select>
              </FormControl>
            </TableCell>
          )}
          {userGroup?.isAdmin && (
            <TableCell
              style={{ padding: "0px !important" }}
              className={styles.patientTableCellAction}
            >
              <IconButton
                onClick={() => {
                  setIsDeleteClicked({
                    status: true,
                    id: row.id,
                    name: row.Patient,
                  });
                }}
                aria-label="delete"
              >
                <DeleteIcon />
              </IconButton>
            </TableCell>
          )}

          <PrecautionsDialog
            open={isPrecautionsDialogOpen}
            onClose={() => setIsPrecautionsDialogOpen(false)}
            onSelect={handlePrecautionsSelect}
            selectedPrecautions={selectedPrecautions}
            precautions={precautionsOptions}
          />
          <RoomStatusDialog
            open={isRoomStatusDialogOpen}
            onClose={() => setIsRoomStatusDialogOpen(false)}
            onSelect={handleRoomStatusSelect}
            currentStatus={roomStatus}
          />
        </TableRow>
      )}
      <DialogConfirm
        open={isNameConflict}
        onClose={(confirm) => handleNameConflictConfirmation(confirm)}
        title="Name Conflict"
        content={`Patient name '${newPatientName}' already exists. Do you want to proceed with the update?`}
      />
      <DialogConfirm
        open={isDeleteClicked.status}
        onClose={(confirm) => handleDelete(isDeleteClicked.id, confirm)}
        title="Confirm Deletion?"
        content={`Patient name '${isDeleteClicked.name}' will be deleted. Do you want to proceed with the update?`}
      />
    </>
  );
};

const PatientTable = ({ data, handleDialogOpen, setSocketRefresh }) => {
  const [tableData, setTableData] = useState([]);

  const [filterPrecautions, setFilterPrecautions] = useState(
    JSON.parse(localStorage.getItem("filterPrecautions")) || ["All"]
  );

  const [filterFloor, setFilterFloor] = useState(
    JSON.parse(localStorage.getItem("filterFloor")) || ""
  );
  const [exportPDF, setExportPDF] = useState(false);
  const { userGroup } = useContext(AuthContext);
  const [maxFloor, setMaxFloor] = useState(0);
  const [isRoomOccupiedWarningOpen, setIsRoomOccupiedWarningOpen] =
    useState(false);

  const [newPatientName, setNewPatientName] = useState("");
  const [prevPatientName, setPrevPatientName] = useState(null);
  const [currentPatientId, setCurrentPatientId] = useState(null);
  useEffect(() => {
    setTableData(data);

    const maxFloorNumber = data.reduce((max, row) => {
      const roomNumber = parseInt(row.Room, 10);
      if (!isNaN(roomNumber)) {
        if (roomNumber >= 0 && roomNumber <= 99) {
          return Math.max(max, 0);
        } else {
          const floor = Math.floor(
            roomNumber / Math.pow(10, Math.floor(Math.log10(roomNumber)))
          );
          return Math.max(max, floor);
        }
      }
      return max;
    }, 0);

    setMaxFloor(maxFloorNumber);
  }, [data]);

  const handleExportPDF = () => {
    setExportPDF(true);
    setTimeout(() => {
      setExportPDF(false);
    }, 100);
  };

  const handlePrecautionsChange = (event) => {
    const value = event.target.value;

    if (value[value.length - 1] === "All" || value.length == 0) {
      setFilterPrecautions(["All"]);
    } else {
      setFilterPrecautions(value.filter((item) => item !== "All"));
    }
  };

  useEffect(() => {
    localStorage.setItem(
      "filterPrecautions",
      JSON.stringify(filterPrecautions)
    );
    localStorage.setItem("filterFloor", JSON.stringify(filterFloor));
  }, [filterPrecautions, filterFloor]);

  const handleUpdateRow = (patientId, field, value) => {
    console.log(patientId, field, value);
    const updatedData = tableData.map((row) => {
      if (row.id === patientId) {
        return { ...row, [field]: value };
      } else return row;
    });

    setTableData(updatedData);
  };

  const filteredData = tableData.filter((row) => {
    const matchPrecautions =
      filterPrecautions.length === 0 ||
      filterPrecautions.includes("All") ||
      filterPrecautions.some((p) => row.Precautions.includes(p));

    const roomNumber = parseInt(row.Room, 10);
    let floorNumber;

    if (!isNaN(roomNumber)) {
      if (roomNumber >= 0 && roomNumber <= 99) {
        floorNumber = "Ground";
      } else if (roomNumber >= 100 && roomNumber <= 999) {
        floorNumber = `Floor ${Math.floor(roomNumber / 100)}`;
      } else if (roomNumber >= 1000 && roomNumber <= 9999) {
        floorNumber = `Floor ${Math.floor(roomNumber / 100)}`;
      } else {
        floorNumber = "Unknown Floor";
      }
    } else {
      floorNumber = "Invalid Room Number";
    }

    const matchFloor =
      filterFloor === "All" ||
      filterFloor === "" ||
      filterFloor === floorNumber;

    return matchPrecautions && matchFloor;
  });

  const handleFloorChange = (event) => {
    const selectedFloor = event.target.value;
    setFilterFloor(selectedFloor);
  };

  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const currentTime = new Date().toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <>
      <Paper elevation={3} className={styles.patientPaper}>
        <Grid container justifyContent="space-between" alignItems="center">
          <div className={styles.flexCenter}>
            <Typography variant="h6" className={styles.marginLeft10}>
              {currentTime} - {currentDate}
            </Typography>
            {userGroup?.isAdmin && (
              <Button
                onClick={handleDialogOpen}
                className={clsx(
                  `${styles.primaryButton} ${styles.createPatientButton}`
                )}
                variant="contained"
                color="primary"
              >
                + Patient
              </Button>
            )}

            {userGroup?.isAdmin && (
              <Button
                onClick={handleExportPDF}
                className={clsx(
                  `${styles.secondaryButton} ${styles.exportPdf}`
                )}
                variant="contained"
                color="primary"
              >
                Export PDF
              </Button>
            )}
          </div>
          <Box>
            {userGroup?.isAdmin && (
              <IconButton onClick={() => setSocketRefresh(true)}>
                <RefreshIcon />
              </IconButton>
            )}
            <FormControl className={styles.filterDropdownFormControl}>
              <InputLabel id="floor-filter-label">Floor</InputLabel>
              <Select
                labelId="floor-filter-label"
                value={filterFloor}
                onChange={handleFloorChange}
                displayEmpty
                MenuProps={{
                  style: {
                    maxHeight: MAX_DROPDOWN_HEIGHT,
                  },
                }}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {Array.from({ length: maxFloor + 10 }, (_, index) => (
                  <MenuItem
                    key={index}
                    value={index === 0 ? "Ground" : `Floor ${index}`}
                  >
                    {index === 0 ? "Ground" : `Floor ${index}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className={styles.filterDropdownFormControl}>
              <InputLabel id="precautions-filter-label">Precautions</InputLabel>
              <Select
                labelId="precautions-filter-label"
                multiple
                value={filterPrecautions}
                onChange={(e) => {
                  handlePrecautionsChange(e);
                }}
                renderValue={(selected) => selected.join(", ")}
                MenuProps={{
                  style: {
                    maxHeight: MAX_DROPDOWN_HEIGHT,
                  },
                }}
              >
                <MenuItem value="All">
                  <em>All</em>
                </MenuItem>
                {precautionsOptions.map((precaution) => (
                  <MenuItem key={precaution} value={precaution}>
                    {precaution}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
      </Paper>

      {exportPDF && <PatientTablePDF data={filteredData} />}
      <TableContainer
        component={Paper}
        className={styles.patientTableContainer}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={styles.patientHeaderCell}>Room</TableCell>

              <TableCell className={styles.patientHeaderCell}>
                Patient
              </TableCell>
              <TableCell className={styles.patientHeaderCell}>
                Pathogen
              </TableCell>
              <TableCell className={styles.patientHeaderCell}>
                Precautions
              </TableCell>
              <TableCell className={styles.patientHeaderCell}>
                Isolation
              </TableCell>
              <TableCell className={styles.patientHeaderCell}>Alerts</TableCell>

              {userGroup?.isAdmin && (
                <TableCell
                  style={{ width: "40px" }}
                  className={styles.patientHeaderCell}
                >
                  Color
                </TableCell>
              )}
              {userGroup?.isAdmin && (
                <TableCell className={styles.patientHeaderCell}>
                  Action
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData?.map((row, index) => (
              <PatientTableRow
                key={row.id}
                index={index}
                row={row}
                onUpdate={handleUpdateRow}
                tableData={tableData}
                isRoomOccupiedWarningOpen={isRoomOccupiedWarningOpen}
                setIsRoomOccupiedWarningOpen={setIsRoomOccupiedWarningOpen}
                newPatientName={newPatientName}
                setNewPatientName={setNewPatientName}
                prevPatientName={prevPatientName}
                setPrevPatientName={setPrevPatientName}
                patientId={row.id}
                currentPatientId={currentPatientId}
                setCurrentPatientId={setCurrentPatientId}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={isRoomOccupiedWarningOpen}
        onClose={() => setIsRoomOccupiedWarningOpen(false)}
      >
        <DialogTitle>Room Occupied Warning</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Another patient is occupying this room currently.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsRoomOccupiedWarningOpen(false)}
            color="primary"
            className={styles.secondaryButton}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PatientTable;
